import { useEffect, useRef, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { useIntakeStatus } from "@/shared/hooks/useIntakeStatus";
import { useMarkIntakeStep } from "@/shared/hooks/useMarkIntakeStep";
import { INTAKE_TASK_IDS } from "@/shared/constants/taskIds";

export type TriageStep =
  | "welcome"
  | "death-certificate"
  | "qr"
  | "request"
  | "scanning"
  | "confirm-details"
  | "authority"
  | "invite"
  | "processing";

interface CompletedUpload {
  documentId: number;
  fileName: string;
  fileSize: number;
  extractionStatus: string | null;
}

interface UseTriageResumeResult {
  step: TriageStep;
  setStep: (step: TriageStep) => void;
  existingDeathCerts: CompletedUpload[] | undefined;
  refetchDeathCerts: () => void;
  requestedFrom: string | null;
  isReady: boolean;
  isResumeResolved: boolean;
  isResumed: boolean;
}

export function useTriageResume(
  estateId: number | undefined,
  open: boolean,
): UseTriageResumeResult {
  const { resumeStep, stepAnswers, isFetched: isIntakeFetched } = useIntakeStatus();
  const markStep = useMarkIntakeStep();
  const hasResumed = useRef(false);
  const [step, setStep] = useState<TriageStep>("welcome");
  const [isResumeResolved, setIsResumeResolved] = useState(false);
  const [isResumed, setIsResumed] = useState(false);

  useEffect(() => {
    if (open) {
      hasResumed.current = false;
      setIsResumeResolved(false);
    }
  }, [open]);

  const { data: existingDeathCerts, refetch: refetchDeathCerts } = useQuery({
    queryKey: ["deathCertDocs", estateId],
    queryFn: async () => {
      const { data } = await supabase
        .from("estate_documents")
        .select("id, file_name, file_size, extraction_status")
        .eq("estate_id", estateId!)
        .eq("document_type", "death_certificate");
      return (data ?? []).map((d) => ({
        documentId: d.id,
        fileName: d.file_name ?? "death_certificate.pdf",
        fileSize: d.file_size ?? 0,
        extractionStatus: d.extraction_status as string | null,
      }));
    },
    enabled: !!estateId && open,
  });

  useEffect(() => {
    if (!open || hasResumed.current) return;
    if (!isIntakeFetched || !existingDeathCerts) return;
    hasResumed.current = true;
    setIsResumeResolved(true);
    setIsResumed((!!resumeStep && resumeStep !== "welcome") || existingDeathCerts.length > 0);

    if (resumeStep && resumeStep !== "welcome") {
      setStep(resumeStep);
    } else if (existingDeathCerts.length > 0 && estateId) {
      const hasCompletedExtraction = existingDeathCerts.some(
        (d) => d.extractionStatus === "completed",
      );
      if (hasCompletedExtraction) {
        markStep.mutate({ taskId: INTAKE_TASK_IDS.INTRO, status: "in_progress" });
        markStep.mutate({ taskId: INTAKE_TASK_IDS.DEATH_CERT, status: "completed" });
        markStep.mutate({ taskId: INTAKE_TASK_IDS.CONFIRM_DETAILS, status: "completed" });
      }
      setStep(hasCompletedExtraction ? "authority" : "scanning");
    }
  }, [open, isIntakeFetched, existingDeathCerts, estateId, resumeStep, markStep]);

  const isReady = isIntakeFetched && (!!existingDeathCerts || !estateId);
  const dcAnswers = stepAnswers(INTAKE_TASK_IDS.DEATH_CERT);
  const requestedFrom = (dcAnswers?.requested_from as string) || null;

  return {
    step,
    setStep,
    existingDeathCerts,
    refetchDeathCerts,
    requestedFrom,
    isReady,
    isResumeResolved,
    isResumed,
  };
}
